import { useContext, useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { findIndex } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ProfileContext } from '../contexts/profileContext';
import {
  getProfileData,
  getUploadingFileData,
  postVideoLink,
  updateVideoRole,
  updateVideoStrength,
  uploadVideo,
  deleteVideo,
  downloadUserVideoWithSubtitles,
  uploadSubtitles,
  generateSubtitles
} from '../service';
import { getVideoUrl } from '../utils/utils';
import { Loader, MultiOptionDropdown, RadioSelect } from './common';
import Button from './common/button';
import { VideoThumbnail } from './common/videoThumbnail';
import { ArchiveIcon, BackButtonIcon, RefreshIcon } from './icons';
import { TrashIcon } from '@heroicons/react/24/solid';
import { PencilLine } from '@phosphor-icons/react';
import Popover from './common/popover';
import { convertJsonToWebvtt, convertWebvttToJson } from '../utils/captions';
import CaptionsEditor from './captionsEditor';
import Swal from 'sweetalert2';

export default function Videos({ profile, groupDetails }) {
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef(null);
  const newVideoRef = useRef(null);
  const videoRef = useRef(null);

  const [adminProfile] = useContext(ProfileContext);

  const {
    masterOrganization: {
      _id: masterOrganizationId,
      customConfigurations: { colors } = {},
      languages
    } = {}
  } = adminProfile;

  const [videos, setVideos] = useState([]);
  const [subtitles, setSubtitles] = useState([]);
  const [roles, setRoles] = useState([]);
  const [strengths, setStrengths] = useState([]);
  const [selectedStrength, setSelectedStrength] = useState({});
  const [selectedRole, setSelectedRole] = useState({});
  const [selectedVideo, setSelectedVideo] = useState({});
  const [newVideoFile, setNewVideoFile] = useState(null);
  const [newVideoUploadURL, setNewVideoUploadURL] = useState(null);
  const [isUploading, setIsUploading] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [isDeletingVideo, setIsDeletingVideo] = useState(false);
  const [downloadingVideo, setDownloadingVideo] = useState({});
  const [subtitleContent, setSubtitleContent] = useState({
    showEditor: false,
    content: null
  });

  const fetchProfileVideos = async () => {
    try {
      const {
        data: { videos: videosData, roles: rolesData, strengths: strengthsData, subtitles }
      } = await getProfileData(profile?._id);

      setVideos(videosData);
      setSubtitles(subtitles || []);

      const currentLanguage = i18n.language;

      // Helper function to map data to dropdown format
      const mapDataForDropdown = (data) =>
        data.map((item) => ({
          _id: item._id,
          label: item[`name_${currentLanguage}`],
          value: item._id
        }));

      const rolesDataForDropdown = mapDataForDropdown(rolesData);
      setRoles(rolesDataForDropdown);

      const strengthsDataForDropdown = mapDataForDropdown(strengthsData);
      setStrengths(strengthsDataForDropdown);
    } catch (error) {
      toast.error('Failed to get profile data');
      console.error('Failed to get profile data:', error);
    }
  };

  useEffect(() => {
    if (selectedVideo) {
      setSelectedRole(selectedVideo.roleId);
      setSelectedStrength(selectedVideo.strengthId);
    }
  }, [selectedVideo]);

  useEffect(() => {
    fetchProfileVideos();
  }, [profile]);

  const handleVideoItemSelect = (selectedVideoId) => {
    clearFileSelection();
    setSubtitleContent({
      showEditor: false
    });

    const itemIndex = findIndex(
      videos,
      ({ videoID: tVideoID }) => parseInt(tVideoID) === selectedVideoId
    );

    if (itemIndex >= 0) {
      setSelectedVideo(videos[itemIndex]);
      return;
    } else {
      // video doesn't exist
      setSelectedVideo({
        videoURL: 'undefined',
        videoID: selectedVideoId,
        isNewVideo: true
      });
    }
  };

  const handleStrengthSelect = async (selectedStrength, video) => {
    try {
      await updateVideoStrength({
        userId: profile?._id,
        strengthId: selectedStrength,
        videoId: video?._id
      });
      toast.success('Video strength updated sucessfully.');

      const updatedStrenghts = cloneDeep(strengths).map((strength) => ({
        ...strength,
        isSelected: strength._id === selectedStrength
      }));

      setSelectedStrength(updatedStrenghts.find((strength) => strength.isSelected));
      setStrengths(updatedStrenghts);

      setSelectedVideo({
        ...video,
        strengthId: selectedStrength
      });
    } catch (error) {
      toast.error('Failed to update strength.');
      console.error('Failed to update strength:', error);
    }
  };

  const handleRoleSelect = async (selectedRole, video) => {
    try {
      await updateVideoRole({
        userId: profile?._id,
        roleId: selectedRole,
        videoId: video?._id
      });
      toast.success('Video strength updated sucessfully.');

      const updatedRoles = cloneDeep(roles).map((role) => ({
        ...role,
        isSelected: role._id === selectedRole
      }));

      setSelectedStrength(updatedRoles.find((role) => role.isSelected));
      setRoles(updatedRoles);

      setSelectedVideo({
        ...video,
        roleId: selectedRole
      });
    } catch (error) {
      toast.error('Failed to update role.');
      console.error('Failed to update role:', error);
    }
  };

  const uploadNewVideo = async () => {
    const videoDuration = newVideoRef.current.duration;
    try {
      setIsUploading(true);
      const vidUrl = selectedVideo?.videoURL;
      const videoName =
        vidUrl && vidUrl !== 'undefined' && vidUrl !== 'null'
          ? vidUrl
          : profile?._id && selectedVideo?.videoID
          ? `${profile?._id}/${selectedVideo?.videoID}`
          : `video-${Date.now()}`; // Generate a random name if all checks fail
      var body = {
        type: newVideoFile.type,
        name: videoName,
        keepOriginal: true,
        isProfileVideo: true
      };
      const {
        data: { url, finalName }
      } = await getUploadingFileData(body);
      const data = await uploadVideo(url, newVideoFile, setUploadProgress, body.type);
      const uploadResponse = await postVideoLink({
        videoURL: finalName,
        videoID: selectedVideo.videoID,
        trimPoints: [0, videoDuration],
        userId: profile?._id
      });
      toast.success(uploadResponse.data.message);
      // setSelectedVideo({ ...selectedVideo, isNewVideo: false });
    } catch (e) {
      console.log('error', e);
      toast.error(e.message);
    } finally {
      fetchProfileVideos();
      setIsUploading(false);
      clearFileSelection();
    }
  };

  const handleFileSelection = (e) => {
    var file = e.target.files[0];
    if (!file) return;
    setNewVideoFile(file);
    setNewVideoUploadURL(URL.createObjectURL(file));
    e.target.value = null;
  };

  const clearFileSelection = () => {
    setNewVideoFile(null);
    setNewVideoUploadURL(null);
  };

  const handleDeleteVideo = async () => {
    try {
      setIsDeletingVideo(true);
      await deleteVideo({ videoId: selectedVideo._id, userId: profile._id });
      toast.success(t('editProfileVideos.delete.success'));

      // Clear selected video
      setSelectedVideo({});
      setVideoToDelete(null);

      fetchProfileVideos();
    } catch (error) {
      toast.error(t('editProfileVideos.delete.failed'));
      console.error('Failed to delete video:', error);
    } finally {
      setIsDeletingVideo(false);
    }
  };

  const handleSaveSubtitles = async (webvttText, lang) => {
    try {
      await uploadSubtitles({
        subtitleTextString: webvttText,
        userId: profile?._id,
        videoId: selectedVideo?.videoID,
        subtitleLangUrl: lang
      });

      await fetchProfileVideos();

      setSubtitleContent({
        showEditor: false,
        content: null,
        error: false,
        saveFunction: null,
        unsavedChanges: false
      });
    } catch (error) {
      console.error('Failed to upload subtitles:', error);
      toast.error('Failed to upload subtitles');
    }
  };

  const selectedVideoDetails = groupDetails?.videos?.find(
    ({ videoPage }) => Number(videoPage.index) === Number(selectedVideo?.videoID)
  );
  const isSomeVideoSelected = Object.keys(selectedVideo)?.length > 0;

  return (
    <>
      {isUploading && <Loader transparent="true" />}
      <div className="mb-8">
        <div className="w-full flex flex-row overflow-x-auto gap-4 px-12 py-4">
          {groupDetails?.videos &&
            groupDetails?.videos.map(({ videoPage }) => (
              <VideoThumbnail
                key={videoPage.index}
                videoId={Number(videoPage.index)}
                videos={videos}
                onSelect={handleVideoItemSelect}
                selectedVideoId={selectedVideo?.videoID}
                isActive={true}
                title={videoPage.profilePageTitle?.[i18n.language]}
                description={videoPage.profilePageDescription?.[i18n.language]}
              />
            ))}
        </div>
      </div>
      {isSomeVideoSelected && (
        <div className="w-full bg-[#F6F6F6] rounded-xl p-6 grid grid-cols-12">
          {!subtitleContent?.showEditor && (
            <>
              <div className="col-span-9">
                <div className="flex w-full justify-center items-center">
                  {selectedVideo?.videoID && !newVideoUploadURL && (
                    <video
                      ref={videoRef}
                      className="max-h-[50dvh] max-w-[90%] rounded-xl"
                      poster={`${process.env.REACT_APP_AWS_URL}${
                        selectedVideoDetails?.videoPage?.thumbnail?.[i18n.language]
                      }`}
                      src={
                        selectedVideo?.videoURL && selectedVideo?.videoURL !== 'undefined'
                          ? getVideoUrl(selectedVideo)
                          : ''
                      }
                      playsInline
                      crossOrigin="anonymous"
                      start={selectedVideo?.trimPoints?.[0] || 0}
                      end={selectedVideo?.trimPoints?.[1] || 0}
                      onLoadedData={() => {
                        // Enable subtitles when video loads
                        if (videoRef.current?.textTracks?.[0]) {
                          videoRef.current.textTracks[0].mode = 'showing';
                          const track = videoRef.current.textTracks[0];
                          if (track.cues) {
                            for (let i = 0; i < track.cues.length; i++) {
                              track.cues[i].line = -3; // Moves subtitles 3 lines up from bottom
                            }
                          }
                        }
                      }}
                      controls>
                      <track
                        kind="subtitles"
                        src={`${process.env.REACT_APP_AWS_URL}${
                          subtitles?.find(
                            (subtitle) => subtitle?.videoId === selectedVideo?.videoID
                          )?.[`url_${i18n.language}`]
                        }?cacheBust=${Date.now()}`}
                        srcLang={i18n.language}
                        label={i18n.language}
                        default
                      />
                    </video>
                  )}
                  {newVideoUploadURL && (
                    <video
                      ref={newVideoRef}
                      src={newVideoUploadURL}
                      className="max-h-[50dvh] max-w-[90%] rounded-xl"
                      playsInline
                      start="0"
                      end="0"
                      controls
                    />
                  )}
                </div>
              </div>
              <div className="col-span-3">
                <div className="bg-white rounded-xl p-6">
                  <p className="text-xl font-bold">
                    {selectedVideoDetails?.videoPage?.profilePageTitle?.[i18n.language]}
                  </p>
                  <p className="text-base text-genericGray-200">
                    {selectedVideoDetails?.videoPage?.profilePageDescription?.[i18n.language]}
                  </p>

                  {/* Strength */}
                  {selectedVideoDetails?.videoPage?.strengthOrRoleSelection?.selectionType ===
                    'strength' &&
                    !selectedVideo.isNewVideo && (
                      <div className="cursor-pointer bg-grey-100 rounded-full text-lg text-black font-bold">
                        <div
                          className="text-base font-normal mt-4"
                          dangerouslySetInnerHTML={{
                            __html: t('editProfileVideos.selectStrengthDescription')
                          }}
                        />
                        <RadioSelect
                          name="strength"
                          selected={selectedStrength}
                          options={[
                            {
                              label: t('editProfileVideos.selectStrength'),
                              value: '',
                              isDisabled: true
                            },
                            ...strengths
                          ]}
                          onChange={(option) => handleStrengthSelect(option, selectedVideo)}
                        />
                      </div>
                    )}
                  {/* Role */}
                  {selectedVideoDetails?.videoPage?.strengthOrRoleSelection?.selectionType ===
                    'role' &&
                    !selectedVideo.isNewVideo && (
                      <div className="cursor-pointer bg-grey-100 rounded-full text-lg text-black font-bold">
                        <div
                          className="text-base font-normal mt-4"
                          dangerouslySetInnerHTML={{
                            __html: t('editProfileVideos.selectRoleDescription')
                          }}
                        />
                        <RadioSelect
                          name="role"
                          selected={selectedRole}
                          options={[
                            {
                              label: t('editProfileVideos.selectRole'),
                              value: '',
                              isDisabled: true
                            },
                            ...roles
                          ]}
                          onChange={(option) => handleRoleSelect(option, selectedVideo)}
                        />
                      </div>
                    )}

                  {/* Download and subtitles */}
                  <div className="w-full flex flex-col gap-6 mb-4 mt-4">
                    <MultiOptionDropdown
                      title={t('editProfileVideos.downloadVideo')}
                      options={[
                        {
                          title: t('editProfileVideos.downloadVideoWithoutSubtitles'),
                          rightIcon: <ArchiveIcon color="#000" />,
                          onClick:
                            selectedVideo?.videoURL && !selectedVideo?.isNewVideo
                              ? async () => {
                                  setDownloadingVideo({
                                    ...downloadingVideo,
                                    [`${selectedVideo?.videoID}-noSubtitles`]: true
                                  });
                                  const downloadVideoUrl = `${process.env.REACT_APP_AWS_URL}profile-videos/${selectedVideo?.videoURL}`;
                                  const response = await fetch(downloadVideoUrl);
                                  const blob = await response.blob();
                                  saveAs(blob, 'video.mp4');
                                  setDownloadingVideo({
                                    ...downloadingVideo,
                                    [`${selectedVideo?.videoID}-noSubtitles`]: false
                                  });
                                }
                              : undefined,
                          loading: downloadingVideo[`${selectedVideo?.videoID}-noSubtitles`]
                        },
                        ...languages
                          .filter((lang) => {
                            const subtitleUrl = subtitles?.find(
                              (subtitle) => subtitle?.videoId === selectedVideo?.videoID
                            )?.[`url_${lang}`];
                            return (
                              subtitleUrl && selectedVideo?.videoURL && !selectedVideo?.isNewVideo
                            );
                          })
                          .map((lang) => ({
                            title: t(`editProfileVideos.downloadVideoWith${lang}Subtitles`),
                            rightIcon: <ArchiveIcon color="#000" />,
                            loading:
                              downloadingVideo[`${selectedVideo?.videoID}-${lang}-subtitles`],
                            onClick: async () => {
                              try {
                                setDownloadingVideo({
                                  ...downloadingVideo,
                                  [`${selectedVideo?.videoID}-${lang}-subtitles`]: true
                                });

                                const subtitlesCodes = {
                                  fr: 'fra',
                                  en: 'eng',
                                  de: 'deu',
                                  it: 'ita'
                                };

                                const downloadVideoTitle = `${profile?.firstName}-video-${selectedVideo?.videoID}-${lang}.mp4`;
                                const payload = {
                                  video_url: `s3://worldskills-videos/profile-videos/${selectedVideo?.videoURL}`,
                                  subs_url: `s3://worldskills-videos/${
                                    subtitles?.find(
                                      (subtitle) => subtitle?.videoId === selectedVideo?.videoID
                                    )?.[`url_${lang}`]
                                  }`,
                                  soft_mux: false,
                                  language: subtitlesCodes[lang]
                                };

                                const { data: { burned_in } = {} } =
                                  await downloadUserVideoWithSubtitles(payload);

                                if (burned_in?.url && new URL(burned_in?.url?.split('?')[0])) {
                                  const response = await fetch(burned_in?.url?.split('?')[0]);
                                  const blob = await response.blob();
                                  saveAs(blob, downloadVideoTitle);
                                } else if (
                                  burned_in?.key &&
                                  new URL(process.env.REACT_APP_AWS_URL + burned_in?.key)
                                ) {
                                  const response = await fetch(
                                    process.env.REACT_APP_AWS_URL + burned_in?.key
                                  );
                                  const blob = await response.blob();
                                  saveAs(blob, downloadVideoTitle);
                                } else {
                                  toast.error('Failed to download video with subtitles');
                                }
                              } catch (error) {
                                console.log({ error });
                                toast.error('Failed to download video with subtitles');
                              } finally {
                                setDownloadingVideo({
                                  ...downloadingVideo,
                                  [`${selectedVideo?.videoID}-${lang}-subtitles`]: false
                                });
                              }
                            }
                          }))
                      ]}
                    />

                    <MultiOptionDropdown
                      title={t('editProfileVideos.editSubtitles')}
                      options={[
                        ...languages.map((lang) => ({
                          title: t(`editProfileVideos.edit${lang}Subtitles`),
                          rightIcon: <PencilLine color="#000" />,
                          onClick: async () => {
                            try {
                              // Get subtitles text from url
                              const subtitlesTextResponse = await fetch(
                                process.env.REACT_APP_AWS_URL +
                                  subtitles?.find(
                                    (subtitle) => subtitle?.videoId === selectedVideo?.videoID
                                  )?.[`url_${lang}`] +
                                  `?cacheBust=${Date.now()}`
                              );
                              if (!subtitlesTextResponse.ok) {
                                // Add cues from scratch
                                setSubtitleContent({
                                  showEditor: true,
                                  content: { cues: [] },
                                  error: false,
                                  saveFunction: handleSaveSubtitles,
                                  lang: lang,
                                  subtitles,
                                  selectedVideo
                                });
                                return;
                              }
                              const subtitlesText = await subtitlesTextResponse.text();
                              const subtitlesJson = convertWebvttToJson(subtitlesText);
                              if (subtitlesJson?.valid) {
                                setSubtitleContent({
                                  showEditor: true,
                                  content: subtitlesJson,
                                  textVttContent: subtitlesText,
                                  saveFunction: handleSaveSubtitles,
                                  error: false,
                                  lang: lang,
                                  subtitles,
                                  selectedVideo
                                });
                              } else {
                                setSubtitleContent({
                                  showEditor: true,
                                  content: subtitlesText,
                                  textVttContent: subtitlesText,
                                  error: true,
                                  errorMessage: subtitlesJson?.error,
                                  saveFunction: handleSaveSubtitles,
                                  lang: lang,
                                  subtitles,
                                  selectedVideo
                                });
                              }
                            } catch (error) {
                              console.error('Failed to get subtitles text:', error);
                              // Add cues from scratch
                              setSubtitleContent({
                                showEditor: true,
                                content: { cues: [] },
                                error: false,
                                saveFunction: handleSaveSubtitles,
                                lang: lang,
                                subtitles,
                                selectedVideo
                              });
                            }
                          }
                        }))
                      ]}
                    />

                    <Button.ButtonOutlined
                      type="button"
                      className="!text-genericGray-200 !bg-backgroundGrey rounded-full !border-backgroundGrey">
                      <label htmlFor="FileInput" className="flex items-center gap-2 cursor-pointer">
                        <RefreshIcon color={'#717171'} /> {t('editProfileVideos.replace')}
                        <div>
                          <input
                            ref={fileInputRef}
                            id="FileInput"
                            className="hidden"
                            type="file"
                            accept="video/*"
                            onChange={handleFileSelection}
                          />
                        </div>
                      </label>
                    </Button.ButtonOutlined>
                    {/* Replace video */}
                    {newVideoUploadURL && (
                      <div className="w-full grid grid-cols-2 gap-4">
                        <Button.ButtonOutlined
                          type="button"
                          className="!border-black !text-black w-full"
                          onClick={clearFileSelection}>
                          {t('editProfileVideos.cancel')}
                        </Button.ButtonOutlined>
                        <Button.ButtonFilled
                          type="button"
                          className="!bg-black w-full"
                          onClick={uploadNewVideo}>
                          {t('editProfileVideos.submit')}
                        </Button.ButtonFilled>
                      </div>
                    )}

                    {selectedVideo?.videoURL && !selectedVideo?.isNewVideo && (
                      <Popover
                        position="left center"
                        onOpen={() => setVideoToDelete(selectedVideo._id)}
                        onClose={() => setVideoToDelete(null)}
                        button={
                          <Button.ButtonOutlined
                            type="button"
                            className="flex items-center gap-2"
                            variant="danger">
                            <TrashIcon className="w-8 h-6" />
                          </Button.ButtonOutlined>
                        }>
                        <div className="px-4 py-2">
                          <h4 className="font-semibold">{t('editProfileVideos.delete.title')}</h4>
                          <p className="text-sm my-2 text-secondary">
                            {t('editProfileVideos.delete.description')}
                          </p>
                          <Button.ButtonFilled
                            onClick={handleDeleteVideo}
                            isMini
                            disabled={isDeletingVideo}>
                            {isDeletingVideo ? (
                              <Loader transparent="true" />
                            ) : (
                              t('editProfileVideos.delete.confirm')
                            )}
                          </Button.ButtonFilled>
                        </div>
                      </Popover>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {subtitleContent?.showEditor && (
            <SubtitlesEditor
              profile={profile}
              videoURL={getVideoUrl(selectedVideo)}
              subtitleContent={subtitleContent}
              setSubtitleContent={setSubtitleContent}
              fetchProfileVideos={fetchProfileVideos}
            />
          )}
        </div>
      )}
    </>
  );
}

function SubtitlesEditor({
  profile,
  videoURL,
  subtitleContent,
  setSubtitleContent,
  fetchProfileVideos
}) {
  const { t, i18n } = useTranslation();

  const handLoadSubtitlesForSpecificLanguage = async ({
    lang,
    forceLoad = false,
    updatedSubtitles = null
  }) => {
    if (subtitleContent?.lang === lang && !forceLoad) return;

    setSubtitleContent({
      ...subtitleContent,
      subtitles: updatedSubtitles || subtitleContent?.subtitles,
      loading: true
    });

    try {
      const { subtitles, selectedVideo } = subtitleContent;
      const subtitlesToUse = updatedSubtitles || subtitles;
      // Get subtitles text from url
      const subtitlesTextResponse = await fetch(
        process.env.REACT_APP_AWS_URL +
          subtitlesToUse?.find((subtitle) => subtitle?.videoId === selectedVideo?.videoID)?.[
            `url_${lang}`
          ] +
          `?cacheBust=${Date.now()}`
      );

      if (!subtitlesTextResponse.ok) {
        throw new Error('Failed to get subtitles text');
      }

      const subtitlesText = await subtitlesTextResponse.text();
      const subtitlesJson = convertWebvttToJson(subtitlesText);
      if (subtitlesJson?.valid) {
        setSubtitleContent({
          ...subtitleContent,
          subtitles: updatedSubtitles || subtitleContent?.subtitles,
          content: subtitlesJson,
          textVttContent: subtitlesText,
          error: false,
          errorMessage: subtitlesJson?.error,
          lang: lang
        });
      } else {
        setSubtitleContent({
          ...subtitleContent,
          subtitles: updatedSubtitles || subtitleContent?.subtitles,
          content: subtitlesText,
          textVttContent: subtitlesText,
          error: true,
          errorMessage: subtitlesJson?.error,
          lang: lang
        });
      }
    } catch (error) {
      console.error('Failed to get subtitles text:', error);
      // Add cues from scratch
      setSubtitleContent({
        ...subtitleContent,
        subtitles: updatedSubtitles || subtitleContent?.subtitles,
        content: { cues: [] },
        error: false,
        lang: lang
      });
    }
  };

  return (
    <>
      {/* Captions Editor - Left Side */}
      <div className="col-span-6 p-4">
        <div className="bg-white p-6">
          <div className="flex items-center gap-4">
            <BackButtonIcon
              onClick={() => {
                if (subtitleContent?.unsavedChanges) {
                  Swal.fire({
                    title: t(`subtitleEdit.unsavedChangesTitle`),
                    text: t('subtitleEdit.unsavedChanges'),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: t(`subtitleEdit.discardChanges`),
                    cancelButtonText: t(`subtitleEdit.cancel`),
                    reverseButtons: true,
                    confirmButtonColor: 'black'
                  }).then((result) => {
                    if (result.isConfirmed) {
                      setSubtitleContent({ showEditor: false, unsavedChanges: false });
                    } else {
                      return;
                    }
                  });
                  return;
                }
                setSubtitleContent({ showEditor: false, unsavedChanges: false });
              }}
            />
            <p className="text-lg text-left font-bold">{t('subtitleEdit.title')}</p>
          </div>
          {/* <p className="text-base mb-4 text-left">{t('subtitleEdit.message')}</p> */}
          {subtitleContent.error && (
            <p className="text-base mb-4 text-left text-cred-400">
              {t('subtitleEdit.invalidWebvtt')} - {subtitleContent.errorMessage}
            </p>
          )}

          {/* AI Generate subtitles */}
          <div className="flex items-center justify-center my-4">
            <Button.ButtonPlatform
              type="button"
              className={'!bg-backgroundGrey !text-black w-full'}
              loading={subtitleContent?.generatingStatus === 'generating'}
              onClick={async () => {
                try {
                  setSubtitleContent({ ...subtitleContent, generatingStatus: 'generating' });
                  const { data: { updatedVideo } = {} } = await generateSubtitles(
                    profile?._id,
                    subtitleContent?.selectedVideo?.videoID,
                    subtitleContent?.lang
                  );

                  setSubtitleContent({
                    ...subtitleContent,
                    subtitles: updatedVideo?.subtitles || subtitleContent?.subtitles,
                    generatingStatus: 'success'
                  });

                  await fetchProfileVideos();

                  handLoadSubtitlesForSpecificLanguage({
                    lang: subtitleContent?.lang,
                    forceLoad: true,
                    updatedSubtitles: updatedVideo?.subtitles
                  });
                } catch (error) {
                  console.error('Failed to generate subtitles:', error);
                  setSubtitleContent({ ...subtitleContent, generatingStatus: 'error' });
                }
              }}>
              <span className="inline-block w-full text-center">
                ✨{' '}
                <span>
                  {!subtitleContent?.generatingStatus
                    ? t('subtitleEdit.generate')
                    : subtitleContent?.generatingStatus === 'generating'
                    ? t('subtitleEdit.generating')
                    : subtitleContent?.generatingStatus === 'success'
                    ? t('subtitleEdit.generatedSuccess')
                    : t('subtitleEdit.generatedError')}
                </span>
              </span>
            </Button.ButtonPlatform>
          </div>
          {/* AI Generate subtitles */}

          {!subtitleContent?.loading && (
            <CaptionsEditor
              subtitleContent={subtitleContent}
              setSubtitleContent={setSubtitleContent}
              initialCues={subtitleContent?.content?.cues}
              showSimpleEditor={subtitleContent.error}
              textVttContent={subtitleContent?.textVttContent}
              setCurrentVideoTime={(currentTime) => {
                setSubtitleContent((prev) => ({ ...prev, currentTime: currentTime }));
              }}
              setPreviewVttContent={(cues) => {
                const webvttText = subtitleContent.error
                  ? cues?.length > 0
                    ? cues
                    : subtitleContent?.content
                    ? subtitleContent?.content
                    : null
                  : convertJsonToWebvtt(cues);
                setSubtitleContent((prev) => ({ ...prev, previewVttContent: webvttText }));
              }}
              onSubmit={async (cues) => {
                if (subtitleContent.saveFunction) {
                  const webvttText = subtitleContent.error ? cues : convertJsonToWebvtt(cues);
                  await subtitleContent.saveFunction(
                    webvttText,
                    subtitleContent?.lang || i18n.language
                  );
                }
              }}
            />
          )}
        </div>
      </div>

      {/* Video - Right Side */}
      <div className="col-span-6 p-4 flex items-center justify-center">
        <SubtitlePreviewVideo
          videoURL={videoURL}
          previewVttContent={subtitleContent?.previewVttContent}
          currentTime={subtitleContent?.currentTime}
        />
      </div>
    </>
  );
}

function SubtitlePreviewVideo({ videoURL, previewVttContent, currentTime }) {
  const videoRef = useRef(null);
  const trackRef = useRef(null);
  const initialVideoURL = useRef(videoURL);

  const updateCues = (vttContent) => {
    const track = trackRef.current;
    if (!track || !vttContent) return;

    const textTrack = track.track;

    // Remove all existing cues
    while (textTrack.cues && textTrack.cues.length > 0) {
      textTrack.removeCue(textTrack.cues[0]);
    }

    // Parse VTT and add new cues
    const lines = vttContent.split('\n');
    let i = 0;

    // Skip WEBVTT header
    while (i < lines.length && !lines[i].includes('-->')) {
      i++;
    }

    while (i < lines.length) {
      const timeLine = lines[i];
      if (timeLine.includes('-->')) {
        const [startStr, endStr] = timeLine.split('-->').map((s) => s.trim());
        const parseTime = (str) => {
          const parts = str.split(':');
          const secParts = parts[parts.length - 1].split('.');
          const seconds = parseFloat(secParts[0]) + parseFloat(secParts[1] || 0) / 1000;
          const minutes = parseFloat(parts[parts.length - 2] || 0);
          const hours = parseFloat(parts[parts.length - 3] || 0);
          return hours * 3600 + minutes * 60 + seconds;
        };

        const start = parseTime(startStr);
        const end = parseTime(endStr);

        // Collect text lines until empty line or next timestamp
        let text = '';
        i++;
        while (i < lines.length && lines[i].trim() !== '' && !lines[i].includes('-->')) {
          if (text) text += '\n';
          text += lines[i];
          i++;
        }

        if (text) {
          const cue = new VTTCue(start, end, text);
          cue.line = -3;
          textTrack.addCue(cue);
        }
      } else {
        i++;
      }
    }

    textTrack.mode = 'showing';
  };

  useEffect(() => {
    if (!previewVttContent) return;
    updateCues(previewVttContent);
  }, [previewVttContent]);

  useEffect(() => {
    if (currentTime) {
      videoRef.current.currentTime = currentTime;
    }
  }, [currentTime]);

  if (!initialVideoURL.current) return null;

  return (
    <video
      ref={videoRef}
      className="max-h-[55dvh] max-w-full rounded-xl"
      src={initialVideoURL.current}
      playsInline
      crossOrigin="anonymous"
      onLoadedData={() => {
        if (videoRef.current?.textTracks?.[0]) {
          videoRef.current.textTracks[0].mode = 'showing';
        }
        updateCues(previewVttContent);
      }}
      controls>
      <track ref={trackRef} kind="subtitles" srcLang="preview" label="Preview" default />
    </video>
  );
}
